import { faThumbtack } from "@fortawesome/pro-regular-svg-icons";

import type { PinPreviewData } from "src/analytics/dashboard/PinChartModal";
import type { MetricsQueryRow } from "src/analytics/metrics/useMetricsQuery";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { Card } from "src/base-components/Card";
import {
  extractTitle,
  isVegaLiteSpec,
  SandboxedChart,
} from "src/chartSandbox/SandboxedChart";
import { Button } from "src/design-system/Button";
import { Tooltip } from "src/design-system/Tooltip";

const PinButton: React.FC<{
  onClick: () => void;
  pinDisabled?: boolean;
  pinDisabledReason?: string;
}> = ({ onClick, pinDisabled, pinDisabledReason }) => {
  const button = (
    <Button
      isDisabled={pinDisabled}
      iconLeft={faThumbtack}
      size="sm"
      variant="primary"
      onClick={onClick}
    >
      Pin
    </Button>
  );
  // A disabled button swallows hover, so wrap a span trigger to keep the tooltip.
  if (pinDisabled && pinDisabledReason) {
    return (
      <Tooltip asChild placement="bottom" title={pinDisabledReason}>
        <span className="inline-flex">{button}</span>
      </Tooltip>
    );
  }
  return button;
};

const ChartCard: React.FC<{
  title: string;
  onRequestPin?: () => void;
  onUnpin?: () => void;
  pinDisabled?: boolean;
  pinDisabledReason?: string;
  children: React.ReactNode;
}> = ({
  title,
  onRequestPin,
  onUnpin,
  pinDisabled,
  pinDisabledReason,
  children,
}) => (
  <Card variant="inverted" className="flex h-full flex-col">
    {/* z-10 keeps the header (and the Pin/Unpin focus ring) above the chart
        canvas in Card.Content, which would otherwise paint over the ring. */}
    <Card.Header className="relative z-10">
      <Card.Title title={title} />
      {onUnpin ? (
        <Button
          iconLeft={faThumbtack}
          size="sm"
          variant="secondary"
          onClick={onUnpin}
        >
          Unpin
        </Button>
      ) : (
        onRequestPin && (
          <PinButton
            onClick={onRequestPin}
            pinDisabled={pinDisabled}
            pinDisabledReason={pinDisabledReason}
          />
        )
      )}
    </Card.Header>
    <Card.Content className="min-h-0 flex-1">{children}</Card.Content>
  </Card>
);

export const ChatChartCard: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
  labelDictionaries?: LabelDictionaries;
  onRequestPin?: (data: PinPreviewData) => void;
  onUnpin?: () => void;
  pinDisabled?: boolean;
  pinDisabledReason?: string;
}> = ({
  rows,
  chartDefinition,
  labelDictionaries,
  onRequestPin,
  onUnpin,
  pinDisabled,
  pinDisabledReason,
}) => {
  if (!isVegaLiteSpec(chartDefinition)) {
    return (
      <Card variant="inverted" className="flex h-full flex-col">
        <SandboxedChart
          chartDefinition={chartDefinition}
          labelDictionaries={labelDictionaries}
          rows={rows}
        />
      </Card>
    );
  }

  const handleRequestPin = onRequestPin
    ? () =>
        onRequestPin({
          defaultTitle: extractTitle(chartDefinition),
          chartDefinition,
          rows,
          labelDictionaries,
        })
    : undefined;

  return (
    <ChartCard
      onRequestPin={handleRequestPin}
      onUnpin={onUnpin}
      pinDisabled={pinDisabled}
      pinDisabledReason={pinDisabledReason}
      title={extractTitle(chartDefinition)}
    >
      <SandboxedChart
        chartDefinition={chartDefinition}
        labelDictionaries={labelDictionaries}
        rows={rows}
      />
    </ChartCard>
  );
};
